import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, memo, useCallback } from "react";
import { CallClient, LocalVideoStream, VideoStreamRenderer, Features } from "@azure/communication-calling";
import { useLiveStreamAppContext } from "../ContextStore/Store";
import Avtar from "./Avtar";
import { BackgroundBlurEffect } from "@azure/communication-calling-effects";
import { StreamMedia, VideoTile } from "@azure/communication-react";
import Permission from "../GeneralFunctions/Permission";
import ClassAppointmentExitPopup from "../PopupModal/EndCallModal";
import { getBrowserPermissionSteps } from "../utils/getBrowserPermissionSteps";

const Camera = forwardRef((props, ref) => {
  const audioRef = useRef(null);
  // Ref (not state) so the effect cleanup always sees the live stream -
  // a state value captured in the closure goes stale and the mic is never released
  const audioStreamRef = useRef(null);

  const {
    speaker,
    microphone,
    toggleStates,
    isCam,
    isMic,
    __merchantId,
    __userId,
    appInsightTrack,
    photoUrl,
    userName,
    camera,
    previewVideoEffects,
    setPreviewVideoEffects,
    setPreviewLocalVideoStream,
    setPreviewRenderer,
    setPreviewView,
    previewView,
    microphonePermission,
    cameraPermission,
    setIsCam
  } = useLiveStreamAppContext();
  const { checkPermissions } = Permission();
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(false);
  useEffect(() => {
    // Check initial permissions
    checkPermissions();

    // Set up interval to check for permission changes
    // (Note: There's no direct event for permission changes, so we poll)
    const interval = setInterval(checkPermissions, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const initializeDeviceManager = async () => {
      try {
        if (camera && cameraPermission === "granted" && isCam) {
          const callClient = new CallClient();
          const deviceManagerInstance = await callClient.getDeviceManager();
          const cameras = await deviceManagerInstance.getCameras();
          if (cameras.length > 0) {
            // Mac Safari can rotate deviceIds, so the stored id may not be in the list -
            // fall back to the first camera rather than passing undefined to LocalVideoStream
            const localStream = new LocalVideoStream(
              cameras.find((item) => item.id === `camera:${camera}`) || cameras[0]
            );
            setPreviewLocalVideoStream(localStream);
            const videoEffectsFeatureApi = localStream.feature(
              Features.VideoEffects
            );
            setPreviewVideoEffects(videoEffectsFeatureApi);
            const renderer = new VideoStreamRenderer(
              videoEffectsFeatureApi?.streamInstance
            );
            setPreviewRenderer(renderer);
            const view = await renderer.createView();
            setPreviewView(view);
          }
        }else if (cameraPermission !== "granted") {
          setIsCam(false)
        }
      } catch (error) {
        console.error("Error initializing DeviceManager:", error);
      }
    }
    initializeDeviceManager();
  }, [camera, cameraPermission, isCam]);

  const handleBlurPreview = useCallback(async () => {
    if (previewVideoEffects && toggleStates.blurBackground) {
      const blurEffect = new BackgroundBlurEffect();
      const isSupportedBlur = await previewVideoEffects?.isSupported(blurEffect);
      if (!isSupportedBlur) {
        console.warn("Background blur is not supported on this device or browser.");
        return;
      }
      await previewVideoEffects?.startEffects(blurEffect);
    } else if (previewVideoEffects && !toggleStates.blurBackground) {
      await previewVideoEffects?.stopEffects();
    }
  }, [previewVideoEffects, toggleStates.blurBackground]);

  useEffect(() => {
    handleBlurPreview();
  }, [handleBlurPreview])

  useEffect(() => {
    let cancelled = false;

    const stopAudio = () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }
    };

    const startAudio = async () => {
      try {
        const newAudioStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: microphone ? { exact: microphone } : undefined },
        });
        if (cancelled) {
          newAudioStream.getTracks().forEach((track) => track.stop());
          return;
        }
        stopAudio();
        audioStreamRef.current = newAudioStream;
        if (audioRef.current) {
          audioRef.current.srcObject = newAudioStream;
          // setSinkId is unavailable on Android browsers and iOS Safari - the OS routes audio there
          if (speaker && typeof audioRef.current.setSinkId === "function") {
            audioRef.current.setSinkId(speaker).catch((err) => {
              console.error("Error setting speaker:", err);
            });
          }
        }
      } catch (err) {
        console.error("Error accessing the microphone:", err);
        appInsightTrack(__merchantId, __userId, "Microphone Accessing Error", "", err);
      }
    };

    if (isMic && microphonePermission === "granted") startAudio();
    else stopAudio();

    return () => {
      cancelled = true;
      stopAudio();
    };
  }, [isMic, microphone, speaker, microphonePermission]);

  // Expose the `toggleLocalVideo` method to the parent
  useImperativeHandle(ref, () => ({}));

  return (
    <>
      <div className="video-size">
        <div className="video-size-content" id="removeVideoTile">
          {isCam ? (
            <VideoTile
              isMirrored={!toggleStates.mirrorMyVideo}
              renderElement={<StreamMedia videoStreamElement={previewView?.target} />}
            />
          ) : (
            <Avtar photoUrl={photoUrl} userName={userName} />
          )}
          <audio ref={audioRef}></audio>
        </div>
      </div>
      <div className="block-controls host-Setting web-host-controls ">
        <div
          className={`controls-btn controls-btn-relative`}
          onClick={() => {
            if (cameraPermission === "granted") {
              props.toggleLocalVideo();
            } else if (cameraPermission !== "granted") {
              setIsCameraEnabled(true);
            }
          }}
          // cameraPermission === "granted" && props.toggleLocalVideo} // Use the function from props
        >
          <i
            className={`icon fas font-awesome-solid-pro va-video ShowHideVideoIcon ${
              cameraPermission !== "granted" && "divDisabled va-video-slash"
            } ${!isCam && "va-video-slash"}`}
            id="cam_icon"
          ></i>
          {cameraPermission !== "granted" && (
            <i
              className="icon font-awesome-solid-pro va-exclamation-triangle mb-icon icon-warning-bell"
              title="Camera and microphone are disabled"
            />
          )}
        </div>
        <div
          className={`controls-btn controls-btn-relative`}
          onClick={() => {
            if (microphonePermission === "granted") {
              props.toggleLocalAudio();
            } else if (microphonePermission !== "granted") {
              setIsMicrophoneEnabled(true);
            }
          }} // Use the function from props
        >
          <i
            className={`icon fas font-awesome-solid-pro va-microphone ShowHideSoundIcon ${
              microphonePermission !== "granted" &&
              "divDisabled va-microphone-slash"
            } ${!isMic && "va-microphone-slash"}`}
            id="mic_icon"
          ></i>
          {microphonePermission !== "granted" && (
            <i
              className="icon font-awesome-solid-pro va-exclamation-triangle mb-icon icon-warning-bell"
              title="Camera and microphone are disabled"
            />
          )}
        </div>
      </div>
      {isMicrophoneEnabled && (
        <ClassAppointmentExitPopup
          show={isMicrophoneEnabled}
          onHide={() => setIsMicrophoneEnabled(false)}
           message={getBrowserPermissionSteps("microphone")}
          buttontext={"Open Browser Settings"}
          headtext={"Allow Vagaro to Access Microphone"}
        />
      )}
      {isCameraEnabled && (
        <ClassAppointmentExitPopup
          show={isCameraEnabled}
          onHide={() => setIsCameraEnabled(false)}
         message={getBrowserPermissionSteps("camera")}
          buttontext={"Open Browser Settings"}
          headtext={"Allow Vagaro to Access Camera"}
        />
      )}
    </>
  );
});
export default memo(Camera);